"use client";

// src/ui/components/calculator/safety-notes.tsx
//
// Trust + safety layer for the Peptidrechner, mounted directly under the inputs.
// Top to bottom (see design §1.7):
//   1. mg/mcg plausibility guard (destructive) — catches the classic
//      "0,5 mg vs 500 mcg" mix-up; one tap applies the mcg interpretation.
//   2. Between-the-marks helper (info/primary, forward only) — when the draw
//      lands between syringe ticks, suggests a water volume that hits a clean IU.
//   3. Overfill/recommendation advisory — restored from the old form banner
//      (peptideCalculator.result.volumeWarning / .syringeRecommendation) when a
//      bigger syringe exists; a separate destructive "no syringe fits" advisory
//      (peptideCalculator.result.syringeNoFit*) when nothing bigger than 1 ml does.
//   4. Calibration U-100 / U-40 segmented control.
//   5. Units explainer <details> — "marks measure volume, not active substance".
// Every warning pairs an icon with text (never color alone); tokens only.

import { type ReactNode, useId } from "react";
import { useTranslations } from "next-intl";
import { AlertTriangle, Info } from "lucide-react";
import { calcReverse, checkMgMcg, formatDecimalDisplay } from "@/lib/calculator/calculations";
import type { CalculatorState } from "@/lib/calculator/use-calculator-state";
import type { CalculationResult, PeptideData } from "@/lib/calculator/types";

const CALIBRATIONS: { value: 100 | 40; label: string }[] = [
	{ value: 100, label: "U-100" },
	{ value: 40, label: "U-40" },
];

interface SafetyNotesProps {
	state: CalculatorState;
	peptide: PeptideData | undefined;
	result: CalculationResult;
	locale: string;
	onDoseChange: (value: number) => void;
	onBacWaterChange: (value: number) => void;
	onCalibrationChange: (value: 40 | 100) => void;
}

export function SafetyNotes({
	state,
	peptide,
	result,
	locale,
	onDoseChange,
	onBacWaterChange,
	onCalibrationChange,
}: SafetyNotesProps) {
	const t = useTranslations("peptideCalculator");
	const calibrationGroupName = useId();
	const fmt = (n: number, d = 1): string => formatDecimalDisplay(n, locale, { min: d, max: d });

	// Bold chunk renderer for rich-text safety copy (mono, tabular numerals).
	const bold = (chunks: ReactNode) => <b className="font-mono tabular-nums">{chunks}</b>;

	// 1) mg/mcg guard — fires when the dose is a 50x+ multiple of the upper range.
	const mgMcg = checkMgMcg(state.dose, peptide?.doseRange?.max);

	// 2) Between-the-marks helper — forward mode only, and only when the live draw
	//    sits meaningfully off a whole IU tick AND the mg/mcg guard isn't showing.
	const iuExact = result.insulinUnits;
	const offMark = Math.abs(iuExact - Math.round(iuExact)) >= 0.08;
	const cleanFix =
		!mgMcg && state.mode === "forward" && offMark
			? calcReverse({ vial: state.peptideAmount, dose: state.dose, profile: "easier" })
			: null;
	// Only suggest if it actually changes the water volume the user has set.
	const showCleanFix = cleanFix != null && Math.abs(cleanFix.bac - state.bacWater) > 0.001;

	const iuRounded = Math.round(iuExact);

	// 3) Overfill advisory — two mutually exclusive states. `noSyringeFits` is the
	//    honest branch: nothing bigger than 1 ml exists, so the copy must offer real
	//    remedies (less BAC water, or splitting the dose) instead of "use a larger
	//    syringe". A null recommendation already implies the draw exceeds every
	//    selectable syringe (the largest is 1 ml), so volumeExceedsSyringe is redundant
	//    here and deliberately omitted — recommendedSyringe === null is the sole signal.
	//    bacWater <= peptideMg / dose keeps a single draw at or under 1 ml. Floor to
	//    0.1 ml so the rounded suggestion can never push the draw back over 1 ml.
	const noSyringeFits = result.recommendedSyringe === null;
	const maxBacWaterMl =
		noSyringeFits && state.dose > 0 ? Math.floor((state.peptideAmount / state.dose) * 10) / 10 : 0;
	// Below 0.3 ml reconstitution is impractical — then only the split advice is honest.
	const canReduceWater = noSyringeFits && maxBacWaterMl >= 0.3 && maxBacWaterMl < state.bacWater;
	const splitCount = noSyringeFits ? Math.max(2, Math.ceil(result.volume - 1e-9)) : 0;

	return (
		<div className="space-y-3">
			{/* 1) mg/mcg plausibility guard (destructive) */}
			{mgMcg && (
				<div className="border-destructive/30 bg-destructive/10 rounded-xl border p-3">
					<div className="flex items-center gap-2 text-sm font-semibold text-destructive">
						<AlertTriangle aria-hidden className="h-4 w-4 shrink-0" />
						<span>{t("safety.guardTitle")}</span>
					</div>
					<p className="mt-1.5 text-sm leading-relaxed text-foreground">
						{t.rich("safety.guardBody", {
							mcg: Math.round(state.dose),
							mg: fmt(mgMcg.suggestMg, 3),
							b: bold,
						})}
					</p>
					<button
						type="button"
						onClick={() => onDoseChange(mgMcg.suggestMg)}
						className="hover:bg-destructive/90 mt-2 inline-flex min-h-11 items-center justify-center rounded-lg bg-destructive px-4 text-sm font-semibold text-destructive-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:scale-[0.99]"
					>
						{t("safety.apply")}
					</button>
				</div>
			)}

			{/* 2) Between-the-marks helper (info/primary, forward only) */}
			{showCleanFix && cleanFix && (
				<div className="rounded-xl border border-info-border bg-info p-3">
					<div className="flex items-center gap-2 text-sm font-semibold text-info-foreground">
						<Info aria-hidden className="h-4 w-4 shrink-0" />
						<span>{t("safety.betweenTitle")}</span>
					</div>
					<p className="mt-1.5 text-sm leading-relaxed text-info-foreground">
						{t.rich("safety.betweenBody", {
							iu: fmt(iuExact, 1),
							ml: fmt(cleanFix.bac, 1),
							target: cleanFix.iu,
							b: bold,
						})}
					</p>
					<button
						type="button"
						onClick={() => onBacWaterChange(cleanFix.bac)}
						className="hover:bg-primary/90 mt-2 inline-flex min-h-11 items-center justify-center rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:scale-[0.99]"
					>
						{t("safety.apply")}
					</button>
				</div>
			)}

			{/* 3a) Overfill / syringe-size advisory (warning) — restored from the old
			    form banner. Uses the existing i18n keys so no safety copy is lost.
			    Only renders when a BIGGER syringe actually exists to switch to. */}
			{result.volumeExceedsSyringe && result.recommendedSyringe !== null && (
				<div className="border-warning/40 bg-warning/10 rounded-xl border p-3">
					<div className="flex items-start gap-2 text-sm leading-relaxed text-foreground">
						<AlertTriangle aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
						<span>
							{t("result.volumeWarning")}{" "}
							<b className="font-medium">
								{t("result.syringeRecommendation")} {result.recommendedSyringe} ml
							</b>
						</span>
					</div>
				</div>
			)}

			{/* 3b) No syringe fits (destructive) — nothing bigger than 1 ml exists, so
			    the copy offers the only honest remedies: less BAC water, or splitting
			    the dose into separate injections. Never suggests a bigger barrel. */}
			{noSyringeFits && (
				<div className="border-destructive/30 bg-destructive/10 rounded-xl border p-3">
					<div className="flex items-center gap-2 text-sm font-semibold text-destructive">
						<AlertTriangle aria-hidden className="h-4 w-4 shrink-0" />
						<span>{t("result.syringeNoFitTitle")}</span>
					</div>
					<p className="mt-1.5 text-sm leading-relaxed text-foreground">
						{t.rich("result.syringeNoFitBody", { ml: fmt(result.volume, 2), b: bold })}
					</p>
					{canReduceWater && (
						<p className="mt-1.5 text-sm leading-relaxed text-foreground">
							{t.rich("result.syringeNoFitWater", { bac: fmt(maxBacWaterMl, 1), b: bold })}
						</p>
					)}
					<p className="mt-1.5 text-sm leading-relaxed text-foreground">
						{t("result.syringeNoFitSplit", { splits: splitCount })}
					</p>
				</div>
			)}

			{/* 4) Calibration + 5) units explainer */}
			<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
				<div className="flex items-center gap-2">
					<span className="text-xs font-medium text-muted-foreground">{t("safety.calibration")}</span>
					<div
						role="radiogroup"
						aria-label={t("safety.calibration")}
						className="bg-muted/50 inline-flex items-center rounded-full border border-border p-0.5"
					>
						{CALIBRATIONS.map((c) => {
							const active = state.calibration === c.value;
							return (
								<label key={c.value} className="cursor-pointer">
									<input
										type="radio"
										name={calibrationGroupName}
										value={c.value}
										checked={active}
										onChange={() => onCalibrationChange(c.value)}
										className="peer sr-only"
									/>
									<span
										className={`inline-flex min-h-11 min-w-11 items-center justify-center rounded-full px-3 text-xs font-semibold transition-colors peer-focus-visible:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-1 ${
											active
												? "bg-primary text-primary-foreground shadow-sm"
												: "text-muted-foreground hover:text-foreground"
										}`}
									>
										{c.label}
									</span>
								</label>
							);
						})}
					</div>
				</div>

				<details className="group min-w-0 flex-1 rounded-xl border border-border bg-card p-2.5 text-sm">
					<summary className="flex cursor-pointer list-none items-center gap-2 font-medium text-foreground [&::-webkit-details-marker]:hidden">
						<Info aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground" />
						<span>{t("safety.explainSummary")}</span>
					</summary>
					<p className="mt-2 leading-relaxed text-muted-foreground">
						{t.rich("safety.explainBody", {
							iu: iuRounded,
							ml: fmt(result.volume, 2),
							b: bold,
						})}
					</p>
				</details>
			</div>

			<p className="text-xs text-muted-foreground">{t("safety.calibrationHint")}</p>
		</div>
	);
}
